<script setup>
import AppLayout from '@/Layouts/AppLayout.vue';
import { QuillEditor } from '@vueup/vue-quill';
import { ref, onMounted } from "vue";
import { useForm, Link } from '@inertiajs/vue3';
import Loading from 'vue-loading-overlay';

const props = defineProps({
    year: String,
    hotelsById: Object,
    flash: Object,
    countries: Object,
    user: Object,
});
let isLoading = ref(false);
let cities = ref([]);

const handleCountryChange = (e) => {
    const country_id = e.target.value;
    const country = props.countries.find((item) => item.id == country_id);
    cities.value = country.cities;
    HotelEditform.city_id = cities.value.sort((a, b) => a.name.localeCompare(b.name))[0].id;
    isLoading.value = true;
    setTimeout(() => {
        isLoading.value = false;
    }, 2000);
};

onMounted(() => {
    const country_id = props.hotelsById?.country_id;
    const country = props.countries.find((item) => item.id == country_id);
    cities.value = country.cities;
    HotelEditform.city_id = props.hotelsById?.city_id;
});

const HotelEditform = useForm({
    name: props.hotelsById?.name,
    latitude: props.hotelsById?.latitude,
    longitude: props.hotelsById?.longitude,
    country_id: props.hotelsById?.country_id,
    city_id: props.hotelsById?.city_id,
    star_rating: props.hotelsById?.star_rating,
    distance_from_airport: props.hotelsById?.distance_from_airport,
    time_from_airport: props.hotelsById?.time_from_airport,
    property_type: props.hotelsById?.property_type,
    check_in: props.hotelsById?.check_in,
    check_out: props.hotelsById?.check_out,
    description: props.hotelsById?.description,
    hotel_image: null,
    hotel_id: props.hotelsById?.id,
});

const HotelEditformSubmit = () => {
    HotelEditform.post('/hotels/update', {
        preserveScroll: true,
        onStart: () => isLoading.value = true,
        onFinish: () => isLoading.value = false,
    });
};

</script>
<style scoped>
@import "../../../../public/ui/vendor/css/vue-quill.snow.css";
@import "vue-loading-overlay/dist/vue-loading.css";
</style>
<template>
    <AppLayout title="Edit Hotel" :year=year :user=user>
        <div class="vld-parent">
            <Loading v-model:active="isLoading" :color="'#696cff'" />
        </div>
        <div class="container-xxl flex-grow-1 container-p-y">
            <div class="card mb-4">
                <h5 class="card-header">Create Hotel</h5>
                <form class="card-body" @submit.prevent="HotelEditformSubmit">
                    <div class="row g-3">
                        <div class="col-md-4">
                            <label for="name" class="form-label"><b>Hotel Name</b></label>
                            <input type="text" class="form-control" name="name" placeholder="Hotel Name"
                                v-model="HotelEditform.name" :class="{ 'is-invalid': HotelEditform.errors.name }">
                            <div v-if="HotelEditform.errors.name" class="invalid-feedback">
                                <div v-text="HotelEditform.errors.name">
                                </div>
                            </div>
                        </div>
                        <div class=" col-md-4">
                            <label for="latitude" class="form-label"><b>Latitude</b></label>
                            <input type="text" class="form-control" name="latitude" placeholder="Latitude"
                                v-model="HotelEditform.latitude" :class="{ 'is-invalid': HotelEditform.errors.latitude }">
                            <div v-if="HotelEditform.errors.latitude" class="invalid-feedback">
                                <div v-text="HotelEditform.errors.latitude">
                                </div>
                            </div>
                        </div>
                        <div class="col-md-4">
                            <label for="longitude" class="form-label"><b>Longitude</b></label>
                            <input type="text" class="form-control" name="longitude" placeholder="Longitude"
                                v-model="HotelEditform.longitude" :class="{ 'is-invalid': HotelEditform.errors.longitude }">
                            <div v-if="HotelEditform.errors.longitude" class="invalid-feedback">
                                <div v-text="HotelEditform.errors.longitude">
                                </div>
                            </div>
                        </div>
                        <div class="col-md-4">
                            <label for="hotel_id" class="form-label"><b>Country</b></label>
                            <select class="form-select" name="country_id" v-model="HotelEditform.country_id"
                                :class="{ 'is-invalid': HotelEditform.errors.country_id }" @change="handleCountryChange">
                                <option value="" disabled="disabled" selected="selected">Select a country ...</option>
                                <option v-for="countries in props.countries" :value="countries.id">
                                    {{ countries.name }}</option>
                            </select>
                            <div v-if="HotelEditform.errors.country_id" class="invalid-feedback">
                                <div v-text="HotelEditform.errors.country_id">
                                </div>
                            </div>
                        </div>
                        <div class="col-md-4">
                            <label for="city_id" class="form-label"><b>City</b></label>
                            <select class="form-select" name="city_id" v-model="HotelEditform.city_id"
                                :class="{ 'is-invalid': HotelEditform.errors.city_id }">
                                <option value="" disabled="disabled" selected="selected">Select a city ...</option>
                                <option v-for="city in cities" :key="city.id" :value="city.id">
                                    {{ city.name }}</option>
                            </select>
                            <div v-if="HotelEditform.errors.city_id" class="invalid-feedback">
                                <div v-text="HotelEditform.errors.city_id">
                                </div>
                            </div>
                        </div>
                        <div class="col-md-4">
                            <label for="star_rating" class="form-label"><b>Star Rating</b></label>
                            <select class="form-select" name="star_rating" v-model="HotelEditform.star_rating"
                                :class="{ 'is-invalid': HotelEditform.errors.star_rating }">
                                <option value="1">1 Star</option>
                                <option value="2">2 Star</option>
                                <option value="3">3 Star</option>
                                <option value="4">4 Star</option>
                                <option value="5">5 Star</option>
                                <option value="6">6 Star</option>
                                <option value="7">7 Star</option>
                            </select>
                            <div v-if="HotelEditform.errors.star_rating" class="invalid-feedback">
                                <div v-text="HotelEditform.errors.star_rating">
                                </div>
                            </div>
                        </div>
                        <div class="col-md-4">
                            <label for="distance_from_airport" class="form-label"><b>Distance From Airport
                                    (Km)</b></label>
                            <input type="number" class="form-control" name="distance_from_airport"
                                v-model="HotelEditform.distance_from_airport" placeholder="Distance From Airport">
                        </div>
                        <div class="col-md-4">
                            <label for="time_from_airport" class="form-label"><b>Time From Airport (min)</b></label>
                            <input type="number" class="form-control" name="time_from_airport"
                                v-model="HotelEditform.time_from_airport" placeholder="Time From Airport">
                        </div>
                        <div class="col-md-4">
                            <label for="property_type" class="form-label"><b>Property Type</b></label>
                            <select class="form-select" name="property_type" v-model="HotelEditform.property_type"
                                :class="{ 'is-invalid': HotelEditform.errors.property_type }">
                                <option value="resort">Resort</option>
                                <option value="guesthouse">Guest House</option>
                            </select>
                            <div v-if="HotelEditform.errors.property_type" class="invalid-feedback">
                                <div v-text="HotelEditform.errors.property_type">
                                </div>
                            </div>
                        </div>
                        <div class="col-md-4">
                            <label for="check_in" class="form-label"><b>Check-in Time</b></label>
                            <input type="time" class="form-control" placeholder="Select Checkin Time" name="check_in"
                                v-model="HotelEditform.check_in">
                        </div>
                        <div class="col-md-4">
                            <label for="check_out" class="form-label"><b>Check-out Time</b></label>
                            <input type="time" class="form-control" placeholder="Select Checkout Time" name="check_out"
                                v-model="HotelEditform.check_out" />
                        </div>
                        <div class="col-md-4">
                            <label for="hotel_image" class="col-form-label"><b>Hotel Cover Image </b><span
                                    class="text-danger">(Unchanged until a new file is uploaded.)</span></label>
                            <input type="file" name="room_image" class="form-control"
                                @input="HotelEditform.hotel_image = $event.target.files[0]"
                                :class="{ 'is-invalid': HotelEditform.errors.hotel_image }">
                            <progress v-if="HotelEditform.progress" :value="HotelEditform.progress.percentage" max="100">
                                {{ HotelEditform.progress.percentage }}%
                            </progress>
                            <div v-if="HotelEditform.errors.hotel_image" class="invalid-feedback">
                                <div v-text="HotelEditform.errors.hotel_image">
                                </div>
                            </div>
                        </div>
                        <div class="col-md-12">
                            <label for="description" class="form-label"><b>Description</b></label>
                            <QuillEditor theme="snow" placeholder="   Enter hotel description"
                                v-model:content="HotelEditform.description" contentType="html" toolbar="essential"
                                object="true" :searchable="true" name="description"
                                :class="{ 'is-invalid': HotelEditform.errors.description }" />
                        </div>

                    </div>
                    <div class="modal-footer border-0 mt-3">
                        <Link type="button" class="btn btn-label-secondary" :href="'/hotels'">
                        Cancel
                        </Link>
                        <button type="submit" class="btn btn-primary me-sm-3 me-1"
                            :disabled="HotelEditform.processing">Update Hotel</button>
                    </div>
                </form>
            </div>
        </div>
    </AppLayout>
</template>